/**
 * AWS Bedrock EU inference profile IDs for the Claude Opus family — the models
 * a scan's analysis can run on.
 *
 * These are **EU** inference profiles (`eu.` prefix), not the global ones
 * (`global.`) and not bare first-party Claude model IDs. All three exist and
 * only the EU set belongs here.
 *
 * The suffixes are not uniform and are not ours to normalise: Opus 5 / 4.8 / 4.7
 * take no suffix, Opus 4.6 ends `-v1`, and Opus 4.5 ends
 * `-20251101-v1:0`. Copy each ID exactly as AWS publishes it — inventing or
 * stripping a suffix produces an ID Bedrock rejects.
 *
 * Ordered newest first. Sonnet, Haiku and Fable profiles are deliberately
 * excluded: this dropdown offers Opus only.
 */

export type AiModelOption = {
  label: string;
  value: string;
  /**
   * Listed but not choosable. Only Opus 4.6 and 4.5 may currently be picked for
   * a scan; the newer profiles stay on the list, greyed out, rather than being
   * deleted from it — see `isSelectableAiModel`.
   */
  disabled?: boolean;
};

export const AI_MODELS: readonly AiModelOption[] = [
  {
    label: 'Claude Opus 5',
    value: 'eu.anthropic.claude-opus-5',
    disabled: true,
  },
  {
    label: 'Claude Opus 4.8',
    value: 'eu.anthropic.claude-opus-4-8',
    disabled: true,
  },
  {
    label: 'Claude Opus 4.7',
    value: 'eu.anthropic.claude-opus-4-7',
    disabled: true,
  },
  { label: 'Claude Opus 4.6', value: 'eu.anthropic.claude-opus-4-6-v1' },
  {
    label: 'Claude Opus 4.5',
    value: 'eu.anthropic.claude-opus-4-5-20251101-v1:0',
  },
] as const;

/** Selected when a scan has no `ai_model` set, and stamped onto new scans. */
export const DEFAULT_AI_MODEL = 'eu.anthropic.claude-opus-4-6-v1';

/**
 * True when `value` is one of the offered profiles — including the disabled
 * ones. This is the "can the select display it" question, which is why it
 * ignores `disabled`: a scan already saved against Opus 5 must keep showing
 * Opus 5 rather than silently reading as 4.6. Use `isSelectableAiModel` for
 * "may this be chosen".
 */
export function isKnownAiModel(value?: string | null): boolean {
  return AI_MODELS.some((model) => model.value === value);
}

/**
 * True when `value` is a profile a scan may actually be switched to. The
 * disabled entries stay on the list so an existing scan's stored model still
 * renders under its own name, but they cannot be picked.
 */
export function isSelectableAiModel(value?: string | null): boolean {
  return AI_MODELS.some((model) => model.value === value && !model.disabled);
}
